/**
 * API Endpoints Configuration
 * All endpoints are now using v2 API for enhanced security
 * Base URL is configured in .env file
 */
const adminEndURL = {
  // Authentication endpoints
  ADMIN_LOGIN: "admin/adminLogin",
  REFRESH_TOKEN: "admin/refreshToken",
  ADMIN_CHANGE_PASSWORD: "admin/changePassword",
  OWNER_RESET_PASSWORD: "admin/owner/resetPassword",

  // Dashboard
  ACTIVE_USERS: "admin/viewDashboard",

  // Key Host Management
  ADD_KEY_HOST: "admin/addKeyHost",
  UPDATE_KEY_HOST: "admin/updateKeyHost",
  GET_KEY_HOST: "admin/getKeyHost",
  GET_KEY_HOST_DETAILS: "admin/getKeyHostDetails",
  GET_KEY_HOST_BOXES: "admin/getKeyHostBoxes",
  GET_KEY_HOST_KEYS: "admin/getKeyHostKeys",
  UPDATE_KEY_HOST_STATUS: "admin/updateKeyHostStatus",
  ADD_KEY_HOST_USER: "admin/addKeyHostUser",
  UPDATE_KEY_HOST_USER: "admin/updateKeyHostUser",
  DELETE_KEY_HOST_USER: "admin/deleteKeyHostUser",
  RESEND_PASS: "admin/resendPassword",
  CHANGE_KEY_HOST_PASSWORD: "admin/changeHostPassword",

  // Key Host Authorization / Capacity
  CHANGE_KEY_AUTHORIZED_HOST: "admin/key/changeAuthorizedHost",
  KEY_HOST_AUTHORIZATION_CHANGES: "admin/keyHostAuthorizationChanges",
  CAPACITY_ALERTS: "admin/capacityAlerts",
  CAPACITY_ALERT_SETTINGS: "admin/capacityAlertSettings",
  LAPSED_SUBSCRIPTION_ALERTS: "admin/lapsedSubscriptionAlerts",
  KEY_DISPOSITION: "admin/key/disposition",

  // Box Management
  ADD_BOX: "admin/addBox",
  UPADTE_BOX: "admin/updateBox",
  DELETE_BOX: "admin/deleteBox",
  GET_BOXES: "admin/getBoxD",
  GET_BOX_LIST: "admin/getBox",
  GET_BOX_DETAIL: "admin/boxDetail",
  GET_BOX_AUDIT_LOGS: "admin/getBoxAuditLogs",
  CUBICLE_STATUS: "admin/action",
  UPDATE_BOX_STATUS: "admin/updateBoxStatus",
  ADD_CUBICLE_DETAIL: "admin/addCubicleDetail",

  // Coupon Management
  ADD_COUPON: "admin/addCoupon",
  GET_COUPONS: "admin/getCoupons",
  UPDATE_COUPON: "admin/editCoupons",
  DELETE_COUPON: "admin/deleteCoupon",

  // Owner Management
  GET_OWNER_LIST: "admin/getKeyOwners",
  GET_OWNER_DETAILS: "admin/ownerDetails",
  UPDATE_OWNER: "admin/updateOwner",
  UPDATE_OWNER_STATUS: "admin/updateOwnerStatus",

  // Key Management
  GET_KEY: "admin/getKeys",
  GET_KEY_DETAILS: "admin/getKeyDetails",
  GET_KEY_INVESTIGATION: "admin/key-investigation",
  DELETE_KEY: "admin/deleteKey",
  ADD_PROJECT_KEYS: "admin/addProjectKeys",
  KEY_HISTORY: "owner/key/history",
  GET_REPORTS: "admin/report-keys",

  // Notification Management
  SEND_NOTIFICAION: "admin/sendNotification",
  // Owners waitlisted for a slot at a specific, full key host (Host.Slot.Requests).
  GET_HOST_REQUESTS: "admin/getHostRequests",
  RESOLVE_HOST_REQUEST: "admin/host-requests/:requestId",

  // Key host requests — the iOS "request a key host near me" flow
  // (key_host_requests collection; POST /owner/requestKeyHost).
  KEY_HOST_REQUESTS: "admin/key-host-requests",
  KEY_HOST_REQUEST_UPDATE: "admin/key-host-requests/:requestId",
  KEY_HOST_REQUEST_ANALYTICS: "admin/key-host-requests/analytics",

  // Subscription Management
  GET_PLANS: "admin/getplans",
  ADD_PLAN: "admin/addPlan",
  TOGGLE_DEPRECATED: "admin/toggleDeprecated",
  PLAN_STATUS: "admin/planStatus",
  DELETE_PLAN: "admin/deletePlan",
  CANCEL_SUBSCRIPTION: "admin/cancelKeySubsacription",
  GET_STRIPE_KEY: "admin/getStripeKey",

  // Content Management
  GET_CONTENT: "admin/getContent",
  UPDATE_CONTENT: "admin/updateContent",

  // Profile Management
  GET_PROFILE: "admin/profile",
  UPDATE_PROFILE: "admin/updateAdminProfile",
  GET_FAVOURITE_LINKS: "admin/favourites",
  TOGGLE_FAVOURITE_LINK: "admin/favourites/toggle",

  // Transaction Management
  GET_TRANSACTION_LOGS: "admin/getTransactionLogs",

  // Query Management
  GET_QUERIES: "admin/query",
  QUERY_ANSWER: "admin/give-answer",

  // Owner Account Management
  OWNER_ACCOUNT_LOGIN: "owner/login",
  OWNER_ACCOUNT_DELETE: "owner/userAccount",

  // Error Log Management (admin tracing)
  ERROR_LOGS: "admin/error-logs",
  ERROR_LOG_RESOLVE: "admin/error-logs/:id/resolve",
  ERROR_LOG_DELETE: "admin/error-logs/:id",

  // Collection Code Audit Logs (invalid scan attempts + usage history)
  COLLECTION_CODE_AUDIT_LOGS: "admin/collection-code-audit-logs",

  // Auth Audit Logs (logon successes, failures, OTP events)
  AUTH_AUDIT_LOGS:       "admin/auth-audit-logs",
  AUTH_AUDIT_LOGS_STATS: "admin/auth-audit-logs/stats",
  AUTH_AUDIT_LOG_DETAIL: "admin/auth-audit-logs/:id",

  // App Check Logs (bot/unattested traffic monitoring)
  APP_CHECK_LOGS: "admin/app-check-logs",

  // Flagged Emails (unverified accounts deleted due to OTP timeout or cleanup job)
  FLAGGED_EMAILS: "admin/flagged-emails",
  FLAGGED_EMAIL_RESOLVE: "admin/flagged-emails/:id/resolve",
  FLAGGED_EMAIL_DELETE: "admin/flagged-emails/:id",

  // Cron Jobs (scheduler status + manual "run now")
  CRON_JOBS: "admin/cronjobs",
  CRON_JOB_EXECUTE: "admin/cronjobs/:id/execute",

  // Host billing (Phase 5 — docs/domain/host-billing.md). Everything is
  // computed live; there is no statement snapshot table.
  HOST_BILLING_SETTINGS: "admin/host-billing/settings",
  HOST_BILLING_OVERRIDES: "admin/host-billing/overrides",
  HOST_BILLING_OVERRIDE: "admin/host-billing/overrides/:keyHostId",
  HOST_BILLING_RATES: "admin/host-billing/rates",
  HOST_BILLING_RATE: "admin/host-billing/rates/:id",
  HOST_BILLING_CYCLES: "admin/host-billing/cycles",
  HOST_BILLING_STATEMENT: "admin/host-billing/statement",
  HOST_BILLING_STATEMENT_EXPORT: "admin/host-billing/statement/export",
  HOST_BILLING_SUMMARY: "admin/host-billing/summary",
  HOST_BILLING_SUMMARY_EXPORT: "admin/host-billing/summary/export",

  // Admin subscription levers (Phase 0 — non-destructive, unlike deleteKey).
  // cancelSubscription doubles as the "resolve a stuck Stripe cancel" path
  // when subscription_cancel_failed_at is set on the key.
  KEY_CANCEL_SUBSCRIPTION: "admin/key/cancelSubscription",
  KEY_REACTIVATE_SUBSCRIPTION: "admin/key/reactivateSubscription",
  // Payment-rework admin tools.
  KEY_COMP: "admin/key/comp",
  KEY_PAY_OUTSTANDING: "admin/key/payOutstanding",
  DISPUTED_KEYS: "admin/disputes",
  DISPUTE_RESOLVE: "admin/disputes/:id/resolve",
};

const ENDPOINTS = {
  ...adminEndURL,
};

export default ENDPOINTS;
